import { createElement, forwardRef, CSSProperties, useCallback, useRef } from 'react';
import { LegendItem } from '../';
import { Flow } from '../../Layouts/Flow';
import { postfixTestId } from '../../../util/testId';
import { TestIdProps } from '../../../util/testId';
import { isTruncated } from '../../../hooks/useTruncationTooltip';
import { useRefArray } from '../../../hooks/useRefArray';
import bundle from '../NovaComponents-Calendar-gui-icu.properties';
import { useDesignTokens, renderInlineCssVars } from '../../../theme';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../../locale/formatting/useFormatLocale';

import cx from 'classnames';
import cn from '../Calendar.css.json';
import '../Calendar.css';

export interface CalendarLegendProps extends TestIdProps {
  className?: string;
  style?: CSSProperties;
  legendItems?: ReadonlyArray<Readonly<LegendItem>>;
  nonWorkDayLegendText?: string;
  specialDayLegendText?: string;
}

const useTextLocalizer = createUseTextLocalizer(bundle);

export const CalendarLegend = forwardRef<HTMLDivElement, CalendarLegendProps>(
  function CalendarLegend(props, ref) {
    const localize = useTextLocalizer();
    const locale = useFormatLocale();

    const { refs: textRefs, createSetRef: createTextRef } = useRefArray<HTMLDivElement>();
    const legendTextRef = useRef<HTMLDivElement | null>(null);

    const day = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
    }).format(new Date(2022, 0, 4));

    const dt = useDesignTokens();
    const tokens = {
      ...renderInlineCssVars(
        {
          'legend-gap': dt.spacing.core.m,
          'font-size-s': dt.fontNova.size.s,
          'font-size-m': dt.fontNova.size.m,
        },
        'calendar-legend'
      ),
    };

    const handleMouseOver = (index: number) => {
      toggleTooltip(index);
    };

    const toggleTooltip = useCallback(
      (index: number) => {
        legendTextRef.current = textRefs[index];
        const div = legendTextRef.current;
        if (!div) {
          return;
        }

        const content = div?.innerText;
        if (content?.length && isTruncated(div, content)) {
          div.setAttribute('title', content);
        } else {
          div.removeAttribute('title');
        }
      },
      [textRefs]
    );

    const getItem = (index: number, className: string[], text: string, id: string) => {
      return (
        <div key={index} className={cx(cn.legendItem)} aria-hidden="true">
          {id === 'specialDay' ? (
            <div
              data-testid={postfixTestId(props.testId, `${id}-legendSquare`)}
              className={cx(cn.legendSquare)}
              aria-hidden="true"
            >
              <div
                data-testid={postfixTestId(props.testId, `${id}-legendSquare`)}
                className={cx(className)}
                aria-hidden="true"
              >
                {day}
              </div>
            </div>
          ) : (
            <div
              data-testid={postfixTestId(props.testId, `${id}-legendSquare`)}
              className={cx(cn.legendSquare, className)}
              aria-hidden="true"
            >
              {day}
            </div>
          )}
          <div
            ref={createTextRef(index)}
            data-testid={postfixTestId(props.testId, `${id}-legendText`)}
            className={cx(cn.legendText)}
            aria-hidden="true"
            onMouseEnter={() => handleMouseOver(index)}
          >
            {text}
          </div>
        </div>
      );
    };

    const getLegendItem = (legendItem: LegendItem) => {
      switch (legendItem) {
        case 'Selected':
          return getItem(1, [cn.legendSelected], localize('selected.label'), 'selected');
        case 'NonWorkday':
          return getItem(
            2,
            [cn.legendNonWorkDay],
            props.nonWorkDayLegendText ?? localize('weekend.label'),
            'weekend'
          );
        case 'NotAvailable':
          return getItem(
            3,
            [cn.legendDisabled, cn.dayCellDisabled],
            localize('notAvailable.label'),
            'notAvailable'
          );
        case 'SpecialDay':
          return getItem(
            4,
            [cn.legendSpecialDay],
            props.specialDayLegendText ?? localize('specialDay.label'),
            'specialDay'
          );
        default:
          return getItem(0, [cn.legendToday], localize('today.label'), 'today');
      }
    };

    const getItemTemplate = (legendArray: LegendItem[]) => {
      return (
        <Flow spacing="xxs" orientation="vertical" ref={ref}>
          <Flow spacing="m" orientation="horizontal">
            {legendArray.slice(0, 2).map((legendItem) => getLegendItem(legendItem))}
          </Flow>
          {legendArray.length > 2 ? (
            <Flow spacing="m" orientation="horizontal">
              {legendArray.slice(2, 4).map((legendItem) => getLegendItem(legendItem))}
            </Flow>
          ) : null}
          {legendArray.length === 5 ? (
            <Flow spacing="m" orientation="horizontal">
              {legendArray.slice(4).map((legendItem) => getLegendItem(legendItem))}
            </Flow>
          ) : null}
        </Flow>
      );
    };

    const getCustomLegendItems = () => {
      if (!props.legendItems) return;
      // Need to put legend items in correct order for display: Today, Selected, NonWorkday, NotAvailable, SpecialDay
      const sortedArray: LegendItem[] = [];
      // eslint-disable-next-line @sas/i18n/jsx-to-localize
      if (props.legendItems.find((element) => element === 'Today')) sortedArray.push('Today');
      // eslint-disable-next-line @sas/i18n/jsx-to-localize
      if (props.legendItems.find((element) => element === 'Selected')) sortedArray.push('Selected');
      if (props.legendItems.find((element) => element === 'NonWorkday'))
        sortedArray.push('NonWorkday');
      if (props.legendItems.find((element) => element === 'NotAvailable'))
        sortedArray.push('NotAvailable');
      if (props.legendItems.find((element) => element === 'SpecialDay'))
        sortedArray.push('SpecialDay');

      return getItemTemplate(sortedArray);
    };

    const getAllLegendItems = () => {
      const allItemsArray: LegendItem[] = [
        'Today',
        'Selected',
        'NonWorkday',
        'NotAvailable',
        'SpecialDay',
      ];
      return getItemTemplate(allItemsArray);
    };

    return (
      <div className={cn.legend} style={tokens}>
        {props.legendItems === undefined ||
        props.legendItems.length === 0 ||
        props.legendItems.length === 5
          ? getAllLegendItems()
          : props.legendItems.length > 0
          ? getCustomLegendItems()
          : null}
      </div>
    );
  }
);
